/**
 * 1.6.5 §2 — the app updates itself (D-1.6.5-2: the person never has to know a new version exists).
 *
 * When the site has a newer version (lib/version.ts) the page reloads **once by itself** — when nothing would be
 * lost: no sheet open, no form dirty, nothing typed on the page (`safeToReload`). Otherwise the banner asks
 * (components/PwaUpdate.tsx), and its Reload — and Settings' "Update now" — is the same path, `press()`.
 *
 * Which reload (`nextStep`):
 *  - 'reload' — an ordinary reload, when the service worker has the new shell in place (lib/sw-update.ts 'ready');
 *  - 'hard'   — past the worker: it is unregistered, its copy of the shell is dropped, and the page goes to its own
 *               address with a cache-busting `_v=<build>` (taken off again when the page opens). This is what
 *               brings the new shell where there is no worker (iOS drops them), where it would not take over, and
 *               where an ordinary reload came back with the old version. A query string alone does not: the old
 *               worker answers every navigation from its cache.
 *
 * It can never loop: what was tried is remembered for the tab's session, keyed by the target build
 * (`abf.updateReload`). One build is worth one ordinary reload and one hard one by itself; after that only a
 * person's press does anything, and a press that still leaves the old version in place is said out loud (`stuck`).
 * If the session cannot remember, nothing happens by itself at all. Offline, nothing happens by itself either —
 * the worker is then the only thing that can still open the app.
 */
import { pageReload } from '@/lib/chunk-reload';
import { anyDirty } from '@/lib/dirty';
import { settleServiceWorker, type WorkerAnswer } from '@/lib/sw-update';
import { versionWatch, type VersionWatch } from '@/lib/version';

export type { WorkerAnswer };

export const UPDATE_KEY = 'abf.updateReload';
/** The cache-busting mark. Not `v`: /volunteers?v=<VolunteerID> is a card. */
export const UPDATE_PARAM = '_v';
/** How long a new worker gets to take over before the page goes past it. */
export const SW_TAKEOVER_MS = 5000;
/** After a try by itself that could not go ahead (offline), the next one waits this long. */
const RETRY_MS = 60 * 1000;
/** A reload that has not happened after this long did not happen: the banner offers Reload again. */
const LEAVING_MS = 10 * 1000;

export type UpdateBy = 'auto' | 'press';
export type UpdateStep = 'reload' | 'hard' | 'none';
export interface UpdateMark {
  build: string;
  stage: 'soft' | 'hard';
  by: UpdateBy;
}

/** A sheet or dialog is open (Radix marks it: components/ui/sheet.tsx, dialog.tsx). */
export function sheetOpen(doc: Document = document): boolean {
  return !!doc.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]');
}

/** May the page reload by itself now — would nothing be lost? */
export function safeToReload(doc: Document = document): boolean {
  return !sheetOpen(doc) && !anyDirty();
}

export function nextStep(by: UpdateBy, build: string, mark: UpdateMark | null, worker: WorkerAnswer): UpdateStep {
  if (worker === 'offline') return by === 'press' ? 'reload' : 'none';
  if (mark && mark.build === build) {
    if (mark.stage === 'soft') return 'hard';
    return by === 'press' ? 'hard' : 'none';
  }
  return worker === 'ready' ? 'reload' : 'hard';
}

/** This page's address with the cache-busting mark: the route, its own query and the hash are kept. */
export function bustUrl(build: string, at: URL = new URL(window.location.href)): string {
  const url = new URL(at.href);
  url.searchParams.set(UPDATE_PARAM, build);
  return url.pathname + url.search + url.hash;
}

/** The address without the mark, or null when it carries none. */
export function stripBust(at: URL = new URL(window.location.href)): string | null {
  if (!at.searchParams.has(UPDATE_PARAM)) return null;
  const url = new URL(at.href);
  url.searchParams.delete(UPDATE_PARAM);
  return url.pathname + url.search + url.hash;
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export function readMark(): UpdateMark | null {
  try {
    const m = JSON.parse(storage()?.getItem(UPDATE_KEY) ?? 'null') as Partial<UpdateMark> | null;
    if (!m || typeof m.build !== 'string' || (m.stage !== 'soft' && m.stage !== 'hard')) return null;
    return { build: m.build, stage: m.stage, by: m.by === 'press' ? 'press' : 'auto' };
  } catch {
    return null;
  }
}

/** True only when the write took: a storage that silently drops it would mean a loop. */
function writeMark(mark: UpdateMark): boolean {
  try {
    const text = JSON.stringify(mark);
    storage()!.setItem(UPDATE_KEY, text);
    return storage()!.getItem(UPDATE_KEY) === text;
  } catch {
    return false;
  }
}

/** Past the worker: unregister it and drop its copy of the app shell (at most 3 s; the navigation goes ahead anyway). */
async function unhookWorker(): Promise<void> {
  const work = (async () => {
    try {
      const regs = (await navigator.serviceWorker?.getRegistrations?.()) ?? [];
      await Promise.all(regs.map((r) => r.unregister()));
    } catch {
      /* no worker */
    }
    try {
      if (typeof caches === 'undefined') return;
      const names = await caches.keys();
      await Promise.all(names.filter((n) => n.startsWith('workbox-precache')).map((n) => caches.delete(n)));
    } catch {
      /* no cache storage */
    }
  })();
  await Promise.race([work, new Promise<void>((r) => window.setTimeout(r, 3000))]);
}

export interface Updater {
  state(): 'idle' | 'updating';
  subscribe(listener: () => void): () => void;
  /** Would nothing be lost by a reload now? */
  safe(): boolean;
  /** Is there still something the page may try by itself for this target? */
  canAuto(): boolean;
  /** A person pressed Reload and the old version is still here after everything was tried. */
  stuck(): boolean;
  /** By itself: true when the page is on its way out. Does nothing when it is not safe. */
  auto(): Promise<boolean>;
  /** The person's press (the banner's Reload, Settings' Update now). */
  press(): Promise<void>;
}

export interface UpdaterOptions {
  watch: VersionWatch;
  settle?: (maxMs: number) => Promise<WorkerAnswer>;
  unhook?: () => Promise<void>;
  safe?: () => boolean;
}

export function createUpdater(o: UpdaterOptions): Updater {
  const { watch } = o;
  const settle = o.settle ?? settleServiceWorker;
  const unhook = o.unhook ?? unhookWorker;
  const safe = o.safe ?? (() => safeToReload());
  const listeners = new Set<() => void>();
  let state: 'idle' | 'updating' = 'idle';
  let running: Promise<boolean> | null = null;
  let retryAt = -Infinity;
  const set = (next: typeof state) => {
    if (state === next) return;
    state = next;
    listeners.forEach((l) => l());
  };
  /** The guard's key: the build the site names; a worker that said so before the site was asked is 'worker'. */
  const build = () => watch.target()?.build ?? 'worker';
  const marked = () => {
    const m = readMark();
    return m && m.build === build() ? m : null;
  };

  async function run(by: UpdateBy): Promise<boolean> {
    set('updating');
    const online = typeof navigator === 'undefined' || navigator.onLine !== false;
    const worker: WorkerAnswer = online ? await settle(SW_TAKEOVER_MS) : 'offline';
    // a sheet opened while the worker was settling: not now
    if (by === 'auto' && !safe()) return false;
    const target = build();
    let step = nextStep(by, target, readMark(), worker);
    if (step === 'none') {
      if (worker === 'offline') retryAt = Date.now() + RETRY_MS;
      return false;
    }
    if (!writeMark({ build: target, stage: step === 'reload' ? 'soft' : 'hard', by: by === 'press' ? 'press' : (marked()?.by ?? 'auto') })) {
      if (by === 'auto') {
        retryAt = Infinity; // the session cannot remember: never by itself
        return false;
      }
      step = worker === 'offline' ? 'reload' : 'hard';
    }
    if (step === 'reload') pageReload.now();
    else {
      await unhook();
      pageReload.to(bustUrl(target));
    }
    // the page is leaving; if it somehow does not, the banner comes back
    window.setTimeout(() => {
      running = null;
      set('idle');
    }, LEAVING_MS);
    return true;
  }

  const start = (by: UpdateBy): Promise<boolean> => {
    if (running) return running;
    const mine = run(by)
      .catch(() => false)
      .then((left) => {
        if (!left) {
          running = null;
          set('idle');
        }
        return left;
      });
    running = mine;
    return mine;
  };

  const canAuto = () => {
    if (!watch.isStale() || Date.now() < retryAt || !storage()) return false;
    return marked()?.stage !== 'hard';
  };

  return {
    state: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    safe,
    canAuto,
    stuck() {
      const m = marked();
      return watch.isStale() && !!m && m.stage === 'hard' && m.by === 'press';
    },
    auto() {
      if (running) return running;
      if (!canAuto() || !safe()) return Promise.resolve(false);
      return start('auto');
    },
    async press() {
      await start('press');
    },
  };
}

const updaters = new WeakMap<VersionWatch, Updater>();
/** One updater per watch: the banner and Settings' line are the same update. */
export function updaterFor(watch: VersionWatch): Updater {
  let u = updaters.get(watch);
  if (!u) {
    u = createUpdater({ watch });
    updaters.set(watch, u);
  }
  return u;
}

/** The app's one updater. */
export const appUpdater: Updater = updaterFor(versionWatch);

/**
 * A page that opened at a cache-busting address takes the mark off before the router reads the address, so no
 * screen ever sees it and a copied link is clean. (This module is loaded before the app renders.)
 */
if (typeof window !== 'undefined') {
  try {
    const clean = stripBust();
    if (clean !== null) window.history.replaceState(window.history.state, '', clean);
  } catch {
    /* the address stays as it is */
  }
}
